"use strict";
/**
 ShapeDiver 3D Viewer Application

 MaterialPropertiesStandard.js

 Content:
   The definition for the standard material.

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialPropertiesStandard
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MaterialPropertiesStandard = void 0;
/**
 * lor including alpha value defined by an array of 4 numbers between 0 and 255 [red, green, blue, alpha]
 * @typedef {Number[]} JSONColor
 */
/**
 * JSON material version 1.0 typedef
 * @typedef {Object} JSONMaterial
 * @version 1.0
 * @variation 1
 * @see module:JSONMaterial~JSONColor
 * @property {String} version - "1.0"
 * @property {module:JSONMaterial~JSONColor} [ambient] - Ambient color
 * @property {module:JSONMaterial~JSONColor} diffuse - Main color
 * @property {module:JSONMaterial~JSONColor} [emission] - Emissive (light) color of the material, essentially a solid color unaffected by other lighting. Default is black.
 * @property {module:JSONMaterial~JSONColor} [specular] - Specular color of the material. Default is a Color set to 0x111111 (very dark grey).
 * @property {Number} [shine] - How shiny the specular highlight is; a higher value gives a sharper highlight. Default is 30.
 * @property {Number} [transparency=0] - Float in the range of 0.0 - 1.0 indicating how transparent the material is. A value of 0.0 indicates fully opaque, 1.0 is fully transparent.
 * @property {String} [bitmaptexture] - Url to an image which will be used as a color texture
 * @property {String} [bumptexture] - Url to an image which will be used as a bump texture
 * @property {String} [transparencytexture] - Url to an image which will be used as a transparency texture
 */
/**
 * Material definiton for the line material, part of the {@link JSONMaterial}
 * @typedef {Object} LineMaterial
 * @property {String} [type='basic'] the type of line, can be 'basic' or 'dashed'
 * @property {Number} [lineWidth=1] the line width (not well supported)
 * @property {Number} [dashSize=3] the dash size for lines of type 'dashed'
 * @property {Number} [gapSize=1] the gap size for lines of type 'dashed'
 * @property {Number} [scale=1] the scale for lines of type 'dashed'
 */
/**
 * JSON material version 2.0 typedef
 * @typedef {Object} JSONMaterial
 * @version 2.0
 * @variation 2
 * @see module:JSONMaterial~JSONColor
 * @property {String} version - "2.0"
 * @property {module:JSONMaterial~JSONColor} color - Material color
 * @property {String} [side] - Which side of the object to display. Options: 'front' - the front side, 'back' - the back side, '' - both sides; Default: both sides
 * @property {Number} [metalness=0.5] - How much the material is like a metal. Non-metallic materials such as wood or stone use 0.0, metallic use 1.0, with nothing (usually) in between. Default is 0.5. A value between 0.0 and 1.0 could be used for a rusty metal look. If metalnesstexture is also provided, both values are multiplied.
 * @property {Number} [roughness=0.5] - How rough the material appears. 0.0 means a smooth mirror reflection, 1.0 means fully diffuse. Default is 0.5. If roughnesstexture is also provided, both values are multiplied.
 * @property {Number} [transparency=0] - Float in the range of 0.0 - 1.0 indicating how transparent the material is. A value of 0.0 indicates fully opaque, 1.0 is fully transparent.
 * @property {Number} [alphaThreshold=0] - Sets the alpha value to be used when running an alpha test. The material will not be renderered if the opacity is lower than this value. Default is 0.
 * @property {String} [bitmaptexture] - URL to the color map. Default is null. The texture map color is modulated by the material color.
 * @property {String} [metalnesstexture] - The blue channel of this texture is used to alter the metalness of the material.
 * @property {String} [roughnesstexture] - The green channel of this texture is used to alter the roughness of the material.
 * @property {String} [bumptexture] - URL to the texture to create a bump map. The black and white values map to the perceived depth in relation to the lights. Bump doesn't actually affect the geometry of the object, only the lighting. If a normal map is defined this will be ignored.
 * @property {String} [normaltexture] - URL to the texture to create a normal map. The RGB values affect the surface normal for each pixel fragment and change the way the color is lit. Normal maps do not change the actual shape of the surface, only the lighting.
 * @property {String} [transparencytexture] - URL to the alpha map, which is a grayscale texture that controls the opacity across the surface (black: fully transparent; white: fully opaque). Default is null.
 * @property {LineMaterial} [line] - the line material definition
 */
/**
 * JSON texture description
 * @typedef {Object} JSONTexture
 * @see module:JSONMaterial~JSONMaterial
 * @property {String} [href] - Link to the bitmap to be used as texture, either href or canvas must be specified
 * @property {Object} [canvas] - Canvas to be used as texture, either href or canvas must be specified
 * @property {Number[]} [offset] - Offset used on uv coordinates for this texture, in each direction U and V
 * @property {Number[]} [repeat] - How many times the texture is repeated across the surface, in each direction U and V.
 * @property {Number} [rotation] - How much the texture is rotated around the center point, in radians. Positive values are counter-clockwise. Default is 0.
 * @property {Number[]} [center] - The point around which rotation occurs. A value of (0.5, 0.5) corresponds to the center of the texture. Default is (0, 0), the lower left.
 */
/**
 * JSON 3D Noise description
 * @typedef {Object} JSONThreeDNoise
 * @see module:JSONMaterial~JSONThreeDNoise
 * @property {Number} [opacity] - The opacity of this effect, where a value of 0 disables this effect (default: 0.0)
 * @property {Number} [noiseID] - The ID for the noise function that is used (range: 0-3, default: 0)
 * @property {Number} [scale] - The scale of the noise (default: 1.0)
 * @property {Number} [distance] - The amount the noise fades with distance (default: 0.0)
 */
/**
 * JSON material version 3.0 typedef
 * @typedef {Object} JSONMaterial
 * @version 3.0
 * @variation 3
 * @see module:JSONMaterial~JSONColor
 * @see module:JSONMaterial~JSONTexture
 * @see module:JSONMaterial~JSONThreeDNoise
 * @property {String} version - "3.0"
 * @property {module:JSONMaterial~JSONColor} color - Material color
 * @property {String} [side] - Which side of the object to display. Options: 'front' - the front side, 'back' - the back side, '' - both sides; Default: both sides
 * @property {Number} [metalness=0.5] - How much the material is like a metal. Non-metallic materials such as wood or stone use 0.0, metallic use 1.0, with nothing (usually) in between. Default is 0.5. A value between 0.0 and 1.0 could be used for a rusty metal look. If metalnesstexture is also provided, both values are multiplied.
 * @property {Number} [roughness=0.5] - How rough the material appears. 0.0 means a smooth mirror reflection, 1.0 means fully diffuse. Default is 0.5. If roughnesstexture is also provided, both values are multiplied.
 * @property {Number} [transparency=0] - Float in the range of 0.0 - 1.0 indicating how transparent the material is. A value of 0.0 indicates fully opaque, 1.0 is fully transparent.
 * @property {Number} [alphaThreshold=0] - Sets the alpha value to be used when running an alpha test. The material will not be renderered if the opacity is lower than this value. Default is 0.
 * @property {Number} [shadowOpacity=1] - The adjustement for the opacity of the shadow. Default is 1.
 * @property {Number} [lightReflectivity=1] - The amount the light is reflected in metal-like surfaces. Default is 1.
 * @property {Number} [bumpAmplitude=1] - How much the bump map affects the material. Typical values between 0 and 1. Default is 1.
 * @property {module:JSONMaterial~JSONThreeDNoise} [threeDNoise] - The options for the 3D Noise effect that can optionally be turned on with its opacity.
 * @property {module:JSONMaterial~JSONTexture} [bitmaptexture] - URL to the color map. Default is null. The texture map color is modulated by the material color.
 * @property {module:JSONMaterial~JSONTexture} [metalnesstexture] - The blue channel of this texture is used to alter the metalness of the material.
 * @property {module:JSONMaterial~JSONTexture} [roughnesstexture] - The green channel of this texture is used to alter the roughness of the material.
 * @property {module:JSONMaterial~JSONTexture} [bumptexture] - URL to the texture to create a bump map. The black and white values map to the perceived depth in relation to the lights. Bump doesn't actually affect the geometry of the object, only the lighting. If a normal map is defined this will be ignored.
 * @property {module:JSONMaterial~JSONTexture} [normaltexture] - URL to the texture to create a normal map. The RGB values affect the surface normal for each pixel fragment and change the way the color is lit. Normal maps do not change the actual shape of the surface, only the lighting.
 * @property {module:JSONMaterial~JSONTexture} [transparencytexture] - URL to the alpha map, which is a grayscale texture that controls the opacity across the surface (black: fully transparent; white: fully opaque). Default is null.
 * @property {LineMaterial} [line] - the line material definition
 */
/**
 Import THREE
 @ignore
*/
var THREE = __importStar(require("../../externals/three"));
/**
 * Utility function to convert from all kinds of string formats to a tinycolor
 * @function
 * @ignore
 */
var TinyColorUtil_1 = require("../../shared/util/TinyColorUtil");
var MaterialPropertiesStandard = /** @class */ (function () {
    function MaterialPropertiesStandard() {
        // #region Properties (25)
        //public alphaThreshold = 0.0;
        this.additionalMaps = [];
        this.alphaMap = null;
        this.bumpMap = null;
        this.bumpScale = 1.0;
        this.color = TinyColorUtil_1.TinyColorUtilInstance.toTinyColor('ffffffff').toThreeColor();
        this.flatShading = false;
        this.lightReflectivity = 1.0;
        this.line = {
            type: 'basic',
            lineWidth: 1,
            dashSize: 3,
            gapSize: 1,
            scale: 1
        };
        this.map = null;
        this.mapPropertyColor = [];
        this.mapPropertyType = [];
        this.metalness = 0.0;
        this.metalnessMap = null;
        this.normalMap = null;
        this.opacity = 1.0;
        this.roughness = 0.5;
        this.roughnessMap = null;
        this.shadowOpacity = 1.0;
        this.side = THREE.DoubleSide;
        this.threeDNoiseDistanceFade = 0.0;
        this.threeDNoiseID = 0;
        this.threeDNoiseOpacity = 0.0;
        this.threeDNoiseScale = 1.0;
        this.transparent = false;
        this.uvTransformAddMap = [];
        // #endregion Public Methods (1)
    }
    // #endregion Properties (25)
    // #region Public Methods (1)
    MaterialPropertiesStandard.prototype.clone = function () {
        return Object.assign({}, this);
    };
    return MaterialPropertiesStandard;
}());
exports.MaterialPropertiesStandard = MaterialPropertiesStandard;
;
